import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ContactManagement from '../components/ContactManagement';
import '../CrmStyles.css';
import {
  LuMail,
  LuPhone,
  LuGlobe,
  LuCreditCard,
  LuBriefcase,
  LuMapPin,
  LuPencil,
  LuCheck,
  LuX
} from 'react-icons/lu';
import { countriesData, getStatesForCountry } from '../../erprecruitment/config/locationConfig';
import BASE_URL from '../../url';

const PortalCompanyOverview = ({ companyId, sessionUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const targetCompanyId = companyId || sessionUser?.company?.companyId;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('portal_token') || localStorage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    };
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!targetCompanyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/crm/companies/${targetCompanyId}`, getAuthHeaders());
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company details in portal:', error);
    } finally {
      setLoading(false);
    }
  }, [targetCompanyId, getAuthHeaders]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleEditClick = () => {
    if (!data?.company) return;
    const c = data.company;
    setFormData({
      email: c.Email || '',
      phone: c.Phone || '',
      website: c.Website || '',
      industryCode: c.IndustryCode || '',
      taxId: c.TaxId || '',
      addressLine1: c.AddressLine1 || '',
      addressLine2: c.AddressLine2 || '',
      city: c.City || '',
      state: c.State || '',
      postalCode: c.PostalCode || '',
      country: c.Country || ''
    });
    setSaveError('');
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setFormData(prev => ({
      ...prev,
      country: selectedCountry,
      state: ''
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const c = data.company;
      const payload = {
        ...formData,
        companyName: c.CompanyName,
        displayName: c.DisplayName,
        isClient: !!c.IsClient,
        isSupplier: !!c.IsSupplier,
        isMSP: !!c.IsMSP,
        isActive: c.IsActive,
        isApproved: c.IsApproved
      };

      const response = await axios.put(`${BASE_URL}/api/crm/companies/${targetCompanyId}`, payload, getAuthHeaders());
      if (response.data.success) {
        setIsEditMode(false);
        setSaveSuccess('Company details updated successfully');
        setTimeout(() => setSaveSuccess(''), 3000);
        fetchDetails();
      } else {
        setSaveError(response.data.message || 'Failed to update company.');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      setSaveError(error.response?.data?.message || 'Error updating company details.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Loading company details...
      </div>
    );
  }

  if (!data || !data.company) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Company details not found.
      </div>
    );
  }

  const { company, contacts } = data;

  const formattedAddress = company.AddressLine1
    ? `${company.AddressLine1}${company.AddressLine2 ? ` ${company.AddressLine2}` : ''}${company.City ? `, ${company.City}` : ''}${company.State ? `, ${company.State}` : ''}${company.PostalCode ? ` ${company.PostalCode}` : ''}${company.Country ? ` ${company.Country}` : ''}`
    : 'N/A';

  return (
    <div style={{ width: '100%' }}>
      {/* Details Card */}
      <div className="crm-card" style={{ marginBottom: '16px' }}>
        <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#334155' }}>
            Company Details
          </h3>
          {!isEditMode ? (
            <button
              type="button"
              onClick={handleEditClick}
              className="crm-btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 14px' }}
            >
              <LuPencil size={14} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCancel}
                className="crm-btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 14px' }}
                disabled={isSaving}
              >
                <LuX size={14} /> Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="crm-btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 14px' }}
                disabled={isSaving}
              >
                <LuCheck size={14} /> {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        <div className="crm-card-body" style={{ padding: '24px' }}>
          {saveError && (
            <div style={{ color: '#ef4444', marginBottom: '16px', fontSize: '14px', background: '#fef2f2', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fecaca' }}>
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div style={{ color: '#065f46', marginBottom: '16px', fontSize: '14px', background: '#ecfdf5', padding: '10px 14px', borderRadius: '6px', border: '1px solid #6ee7b7' }}>
              {saveSuccess}
            </div>
          )}

          {!isEditMode ? (
            <div className="crm-details-grid">
              <div className="crm-detail-item">
                <span className="crm-detail-label">EMAIL</span>
                <span className="crm-detail-value">
                  <LuMail size={16} color="#9ca3af" /> {company.Email || 'N/A'}
                </span>
              </div>

              <div className="crm-detail-item">
                <span className="crm-detail-label">PHONE</span>
                <span className="crm-detail-value">
                  <LuPhone size={16} color="#9ca3af" /> {company.Phone || 'N/A'}
                </span>
              </div>

              <div className="crm-detail-item">
                <span className="crm-detail-label">WEBSITE</span>
                <span className="crm-detail-value">
                  <LuGlobe size={16} color="#9ca3af" style={{ flexShrink: 0 }} />
                  {company.Website ? (
                    <a
                      href={company.Website.startsWith('http') ? company.Website : `https://${company.Website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3b5bfd', textDecoration: 'none' }}
                      onMouseOver={(e) => (e.target.style.textDecoration = 'underline')}
                      onMouseOut={(e) => (e.target.style.textDecoration = 'none')}
                    >
                      {company.Website}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>

              <div className="crm-detail-item">
                <span className="crm-detail-label">INDUSTRY</span>
                <span className="crm-detail-value">
                  <LuBriefcase size={16} color="#9ca3af" /> {company.IndustryCode || 'N/A'}
                </span>
              </div>

              <div className="crm-detail-item">
                <span className="crm-detail-label">TAX ID</span>
                <span className="crm-detail-value">
                  <LuCreditCard size={16} color="#9ca3af" /> {company.TaxId ? company.TaxId : 'N/A'}
                </span>
              </div>

              <div className="crm-detail-item" style={{ gridColumn: 'span 2' }}>
                <span className="crm-detail-label">ADDRESS</span>
                <span className="crm-detail-value" style={{ wordBreak: 'break-word', alignItems: 'flex-start' }}>
                  <LuMapPin size={16} color="#9ca3af" style={{ flexShrink: 0, marginTop: '3px' }} />
                  <span>{formattedAddress}</span>
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div className="crm-grid-2" style={{ gap: '16px' }}>
                <div className="crm-form-group">
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Email</label>
                  <input
                    type="email"
                    name="email"
                    className="crm-input"
                    value={formData.email || ''}
                    onChange={handleChange}
                    placeholder="Enter email"
                  />
                </div>
                <div className="crm-form-group">
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    className="crm-input"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="crm-grid-2" style={{ gap: '16px', marginTop: '12px' }}>
                <div className="crm-form-group">
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Website</label>
                  <input
                    type="text"
                    name="website"
                    className="crm-input"
                    value={formData.website || ''}
                    onChange={handleChange}
                    placeholder="e.g. www.example.com"
                  />
                </div>
                <div className="crm-form-group">
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Industry</label>
                  <select
                    name="industryCode"
                    className="crm-select"
                    value={formData.industryCode || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Industry</option>
                    <option value="TECHNOLOGY">Technology</option>
                    <option value="HEALTHCARE">Healthcare</option>
                    <option value="FINANCE">Finance</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              <div className="crm-form-group" style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Tax ID</label>
                <input
                  type="text"
                  name="taxId"
                  className="crm-input"
                  value={formData.taxId || ''}
                  onChange={handleChange}
                  placeholder="Enter Tax ID / EIN"
                  style={{ maxWidth: '300px' }}
                />
              </div>

              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', fontWeight: 600 }}>Address</h4>
                <div className="crm-grid-2" style={{ gap: '16px' }}>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Street Address</label>
                    <input
                      type="text"
                      name="addressLine1"
                      className="crm-input"
                      value={formData.addressLine1 || ''}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Apartment, suite, etc.</label>
                    <input
                      type="text"
                      name="addressLine2"
                      className="crm-input"
                      value={formData.addressLine2 || ''}
                      onChange={handleChange}
                      placeholder="Suite, unit, building, floor, etc."
                    />
                  </div>
                </div>

                <div className="crm-grid-2" style={{ gap: '16px', marginTop: '12px' }}>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Country</label>
                    <select
                      name="country"
                      className="crm-select"
                      value={formData.country || ''}
                      onChange={handleCountryChange}
                    >
                      <option value="">Select Country</option>
                      {countriesData.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>State / Province</label>
                    <select
                      name="state"
                      className="crm-select"
                      value={formData.state || ''}
                      onChange={handleChange}
                      disabled={!formData.country}
                    >
                      <option value="">Select State</option>
                      {formData.country && getStatesForCountry(formData.country).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="crm-grid-2" style={{ gap: '16px', marginTop: '12px' }}>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>City</label>
                    <input
                      type="text"
                      name="city"
                      className="crm-input"
                      value={formData.city || ''}
                      onChange={handleChange}
                      placeholder="City"
                    />
                  </div>
                  <div className="crm-form-group">
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      className="crm-input"
                      value={formData.postalCode || ''}
                      onChange={handleChange}
                      placeholder="Postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contacts Card */}
      <ContactManagement
        companyId={targetCompanyId}
        contacts={contacts || []}
        onRefresh={fetchDetails}
        isEditMode={isEditMode}
        isPortal={true}
      />
    </div>
  );
};

export default PortalCompanyOverview;
